import { useId, useState } from "react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  COUNTRIES,
  DEFAULT_COUNTRY,
  findCountryByDialCode,
  stripTrunkPrefix,
  TRUNK_PREFIX_HINT,
  type Country,
} from "@/lib/phoneValidation";

export { COUNTRIES, DEFAULT_COUNTRY };
export type { Country };

interface PhoneInputProps {
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  hasError?: boolean;
  id?: string;
}

/**
 * Phone input with country selector. Outputs a single string in +CCXXXXXXXX format.
 * The `value` and `onChange` operate on the full E.164-style number.
 *
 * A national trunk 0 ("070 123 45 67" in Sweden) is dropped from the local
 * digits, so the output is +46701234567 rather than +460701234567, and a
 * hint under the field says so (#1059). A value saved before that fix is
 * shown without its 0 too; it is rewritten on the next edit.
 */
export function PhoneInput({
  value = "",
  onChange,
  disabled = false,
  hasError = false,
  id,
}: PhoneInputProps) {
  const fallbackId = useId();
  const hintId = `${id ?? fallbackId}-trunk-hint`;

  // The picked country; the value's own dial code wins when it has one, so a
  // value loaded after mount (a resumed signup) shows the right country.
  const [pickedCountry, setPickedCountry] = useState<Country>(
    () => findCountryByDialCode(value) ?? DEFAULT_COUNTRY
  );
  const selectedCountry = findCountryByDialCode(value) ?? pickedCountry;
  const [trunkDropped, setTrunkDropped] = useState(false);

  // The local number portion (without dial code and trunk prefix)
  const rawLocal = value.startsWith(selectedCountry.dialCode)
    ? value.slice(selectedCountry.dialCode.length)
    : value.startsWith("+")
      ? ""
      : value;
  const localNumber = stripTrunkPrefix(selectedCountry, rawLocal);
  const showTrunkHint = trunkDropped || localNumber !== rawLocal;

  const handleCountryChange = (countryCode: string) => {
    const country = COUNTRIES.find(c => c.code === countryCode);
    if (!country) {
      return;
    }
    setPickedCountry(country);
    const local = stripTrunkPrefix(country, localNumber);
    setTrunkDropped(local !== localNumber);
    // Rebuild the full number with the new dial code
    onChange?.(country.dialCode + local);
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Strip all non-digit characters from the local part, then a trunk 0
    const digits = e.target.value.replace(/\D/g, "");
    const local = stripTrunkPrefix(selectedCountry, digits);
    if (local !== digits) {
      setTrunkDropped(true);
    } else if (digits === "") {
      setTrunkDropped(false);
    }
    onChange?.(selectedCountry.dialCode + local);
  };

  return (
    <>
      <div
        className={cn("flex gap-0 rounded-md", hasError && "ring-1 ring-destructive rounded-md")}
      >
        {/* Country selector */}
        <Select
          value={selectedCountry.code}
          onValueChange={handleCountryChange}
          disabled={disabled}
        >
          <SelectTrigger
            className={cn(
              "w-[110px] shrink-0 rounded-r-none border-r-0 focus:z-10",
              hasError && "border-destructive"
            )}
            aria-label="Select country"
          >
            <SelectValue>
              <span className="flex items-center gap-1.5">
                <span role="img" aria-label={selectedCountry.name}>
                  {selectedCountry.flag}
                </span>
                <span className="text-muted-foreground text-xs">{selectedCountry.dialCode}</span>
              </span>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {COUNTRIES.map(country => (
              <SelectItem key={country.code} value={country.code}>
                <span className="flex items-center gap-2">
                  <span role="img" aria-label={country.name}>
                    {country.flag}
                  </span>
                  <span>{country.name}</span>
                  <span className="text-muted-foreground text-xs ml-auto pl-2">
                    {country.dialCode}
                  </span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Number input */}
        <Input
          id={id}
          type="tel"
          inputMode="numeric"
          placeholder={selectedCountry.placeholder}
          value={localNumber}
          onChange={handleNumberChange}
          disabled={disabled}
          className={cn(
            "rounded-l-none flex-1 transition-all duration-200",
            hasError && "border-destructive focus-visible:ring-destructive"
          )}
          aria-label="Phone number"
          aria-describedby={showTrunkHint ? hintId : undefined}
        />
      </div>
      {showTrunkHint && (
        <p id={hintId} role="status" className="text-xs text-muted-foreground">
          {TRUNK_PREFIX_HINT}
        </p>
      )}
    </>
  );
}
